import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Package,
  Lock,
  Loader2,
  LogOut,
  RefreshCw,
  Search,
  FileImage,
  Inbox,
  Paperclip,
  ScanLine,
  Printer,
  Trash2,
  StickyNote,
  Mail,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { COUNTRY_META, COUNTRIES } from "@/lib/countries";

const ESTATUS = [
  "Iniciado",
  "Paquete conseguido y cargado en Slack",
  "Paquete no conseguido en sistema de escaneo",
  "Paquete no conseguido en almacén",
  "Reporte Cerrado Satisfactoriamente",
  "Reporte Cerrado No satisfactoriamente",
];

const CERRADO = new Set([
  "Reporte Cerrado Satisfactoriamente",
  "Reporte Cerrado No satisfactoriamente",
]);

function fmtDuration(sec) {
  if (sec == null) return "—";
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function estatusColor(s) {
  switch (s) {
    case "Paquete conseguido y cargado en Slack":
      return "border-blue-300 bg-blue-50 text-blue-800";
    case "Paquete no conseguido en sistema de escaneo":
    case "Paquete no conseguido en almacén":
      return "border-amber-300 bg-amber-50 text-amber-800";
    case "Reporte Cerrado Satisfactoriamente":
      return "border-green-300 bg-green-50 text-green-800";
    case "Reporte Cerrado No satisfactoriamente":
      return "border-alert/40 bg-alert/10 text-alert";
    default:
      return "border-ink/15 bg-white text-ink";
  }
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "ehv_admin_token";

function authHeaders() {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onLogin(data.user);
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-inkdeep flex items-center justify-center px-5" data-testid="admin-login">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md"
      >
        <div className="flex items-center gap-2.5 mb-8">
          <span className="flex h-9 w-9 items-center justify-center rounded-sm bg-gold">
            <Package className="h-5 w-5 text-ink" strokeWidth={2.4} />
          </span>
          <span className="font-display text-xl font-extrabold text-white tracking-tight">
            Envíos<span className="text-gold">·</span>Venezuela
          </span>
        </div>
        <div className="bg-white rounded-md p-8 shadow-2xl border border-white/10">
          <div className="flex items-center gap-2 mb-1 text-ink">
            <Lock className="h-4 w-4 text-gold" />
            <span className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Panel de reclamos
            </span>
          </div>
          <h1 className="font-display text-3xl font-extrabold text-ink tracking-tight mb-6">
            Acceso administrador
          </h1>
          <form onSubmit={submit} className="flex flex-col gap-4">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Correo"
              data-testid="admin-email"
              className="h-12 bg-paper border border-border rounded-sm px-4 text-ink outline-none focus:border-ink transition-colors"
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Contraseña"
              data-testid="admin-password"
              className="h-12 bg-paper border border-border rounded-sm px-4 text-ink outline-none focus:border-ink transition-colors"
            />
            {error && (
              <p className="text-sm text-alert font-medium" data-testid="admin-login-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-button"
              className="h-12 rounded-sm bg-gold font-semibold text-ink inline-flex items-center justify-center gap-2 transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-70"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Ingresar"}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="bg-white border border-ink/10 rounded-md p-6">
      <div className="font-display text-4xl font-extrabold text-ink tracking-tight">{value}</div>
      <div className="mt-1 text-sm text-muted-foreground">{label}</div>
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const [claims, setClaims] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [estatusFilter, setEstatusFilter] = useState("Todos");
  const [paisFilter, setPaisFilter] = useState("Todos");
  const [view, setView] = useState("activos");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 15;
  const [resendingId, setResendingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [updatingEstatusId, setUpdatingEstatusId] = useState(null);
  const [notesClaimId, setNotesClaimId] = useState(null);
  const [noteInput, setNoteInput] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [emailClaimId, setEmailClaimId] = useState(null);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  const changeEstatus = async (claim, nuevo) => {
    if (nuevo === (claim.estatus || "Iniciado")) return;
    setUpdatingEstatusId(claim.id);
    try {
      const { data } = await axios.patch(
        `${API}/admin/claims/${claim.id}/estatus`,
        { estatus: nuevo },
        { headers: authHeaders() }
      );
      const closed = CERRADO.has(nuevo);
      setClaims((prev) =>
        prev.map((c) =>
          c.id === claim.id
            ? {
                ...c,
                estatus: nuevo,
                archived: closed,
                closed_at: closed ? new Date().toISOString() : null,
              }
            : c
        )
      );
      axios
        .get(`${API}/admin/stats`, { headers: authHeaders() })
        .then((r) => setStats(r.data))
        .catch(() => {});
      if (closed) {
        toast.success("Reclamo cerrado y archivado");
      } else if (data.webhook_delivered) {
        toast.success(`Estatus actualizado · webhook enviado`);
      } else {
        toast.message(data.message || "Estatus actualizado");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo actualizar el estatus");
    } finally {
      setUpdatingEstatusId(null);
    }
  };

  const addNote = async () => {
    const texto = noteInput.trim();
    if (!texto || !notesClaimId) return;
    setAddingNote(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/claims/${notesClaimId}/notas`,
        { texto },
        { headers: authHeaders() }
      );
      setClaims((prev) =>
        prev.map((c) =>
          c.id === notesClaimId
            ? { ...c, notas: [...(c.notas || []), data.nota] }
            : c
        )
      );
      setNoteInput("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo agregar la nota");
    } finally {
      setAddingNote(false);
    }
  };

  const deleteNote = async (notaId) => {
    try {
      await axios.delete(`${API}/admin/claims/${notesClaimId}/notas/${notaId}`, {
        headers: authHeaders(),
      });
      setClaims((prev) =>
        prev.map((c) =>
          c.id === notesClaimId
            ? { ...c, notas: (c.notas || []).filter((n) => n.id !== notaId) }
            : c
        )
      );
    } catch {
      toast.error("No se pudo eliminar la nota");
    }
  };

  const openEmail = (claim) => {
    setEmailClaimId(claim.id);
    setEmailSubject(`Sobre tu reclamo ${claim.numero_reclamo || ""}`.trim());
    setEmailBody("");
  };

  const sendEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim() || !emailClaimId) return;
    setSendingEmail(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/claims/${emailClaimId}/send-email`,
        { asunto: emailSubject.trim(), mensaje: emailBody.trim() },
        { headers: authHeaders() }
      );
      if (data.webhook_delivered) {
        toast.success("Correo enviado al flujo de Zapier");
        setEmailClaimId(null);
      } else {
        toast.error(data.message || "No se pudo enviar el correo");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar el correo");
    } finally {
      setSendingEmail(false);
    }
  };

  const resend = async (id, numero) => {
    setResendingId(id);
    try {
      const { data } = await axios.post(
        `${API}/admin/claims/${id}/resend-webhook`,
        {},
        { headers: authHeaders() }
      );
      if (data.webhook_delivered) {
        toast.success(`Webhook reenviado a Zapier${numero ? ` · ${numero}` : ""}`);
      } else {
        toast.error(data.message || "No se pudo confirmar el envío a Zapier");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo reenviar el webhook");
    } finally {
      setResendingId(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        axios.get(`${API}/admin/claims`, { headers: authHeaders() }),
        axios.get(`${API}/admin/stats`, { headers: authHeaders() }),
      ]);
      setClaims(c.data);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        onLogout();
      } else {
        toast.error("Error cargando reclamos");
      }
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const viewFile = async (path) => {
    try {
      const res = await axios.get(`${API}/files/${path}`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
    } catch {
      toast.error("No se pudo abrir el archivo");
    }
  };

  const deleteClaim = async (id, numero) => {
    if (
      !window.confirm(
        `¿Eliminar el reclamo ${numero || ""}? Esta acción no se puede deshacer.`
      )
    )
      return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/admin/claims/${id}`, { headers: authHeaders() });
      setClaims((prev) => prev.filter((c) => c.id !== id));
      toast.success("Reclamo eliminado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    } finally {
      setDeletingId(null);
    }
  };

  const printClaim = async (c) => {
    const esc = (s) =>
      String(s ?? "—").replace(/[&<>"]/g, (ch) => ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
      }[ch]));

    let imgSection = "";
    if (c.screenshot_path) {
      try {
        const res = await axios.get(`${API}/files/${c.screenshot_path}`, {
          headers: authHeaders(),
          responseType: "blob",
        });
        const dataUrl = await new Promise((resolve) => {
          const fr = new FileReader();
          fr.onload = () => resolve(fr.result);
          fr.onerror = () => resolve("");
          fr.readAsDataURL(res.data);
        });
        if (String(dataUrl).startsWith("data:image")) {
          imgSection = `<div class="block"><div class="lbl">Adjunto</div><img class="shot" src="${dataUrl}"/></div>`;
        }
      } catch {
        /* ignore attachment errors */
      }
    }

    const fechaPrint = new Date().toLocaleString("es-VE", {
      dateStyle: "medium",
      timeStyle: "short",
    });

    const w = window.open("", "_blank");
    if (!w) {
      toast.error("Permite las ventanas emergentes para imprimir");
      return;
    }
    const cfg = COUNTRIES[c.pais] || COUNTRIES.venezuela;
    const html = `<!doctype html><html lang="es"><head><meta charset="utf-8"/>
<title>Reclamo ${esc(c.numero_reclamo)}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#071E22;padding:32px;max-width:820px;margin:0 auto}
  .top{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #071E22;padding-bottom:16px;margin-bottom:24px}
  .top img{height:52px}
  .top .meta{text-align:right;font-size:12px;color:#555}
  h1{font-size:20px;letter-spacing:.5px;text-transform:uppercase;margin-bottom:4px}
  .hero{display:flex;gap:24px;margin-bottom:24px}
  .hero .cell{flex:1;border:2px solid #071E22;border-radius:8px;padding:14px 16px}
  .hero .cell .k{font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#666}
  .hero .cell .v{font-size:26px;font-weight:800;font-family:monospace;margin-top:4px;word-break:break-all}
  .hero .cell.gold{background:#F2C94C;border-color:#F2C94C}
  .grid{display:grid;grid-template-columns:1fr 1fr;gap:14px 32px;margin-bottom:24px}
  .field .lbl,.block .lbl{font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#666;margin-bottom:2px}
  .field .val{font-size:16px;font-weight:600;word-break:break-word}
  .block{border:1px solid #ccc;border-radius:8px;padding:14px 16px;margin-bottom:16px}
  .block .txt{font-size:15px;line-height:1.5;white-space:pre-wrap}
  .shot{max-width:100%;max-height:420px;margin-top:8px;border:1px solid #ccc;border-radius:6px}
  .foot{margin-top:28px;border-top:1px solid #ccc;padding-top:12px;font-size:11px;color:#888;display:flex;justify-content:space-between}
  @media print{body{padding:0}}
</style></head><body>
  <div class="top">
    <img src="${window.location.origin}${cfg.logoColor}" alt="logo"/>
    <div class="meta">${esc(cfg.marca)} · Hoja de reclamo<br/>Impreso: ${esc(fechaPrint)}</div>
  </div>
  <h1>Hoja de reclamo · ${esc(cfg.nombre)}</h1>
  <div class="hero">
    <div class="cell gold"><div class="k">Nº de reclamo</div><div class="v">${esc(c.numero_reclamo)}</div></div>
    <div class="cell"><div class="k">Tracking / Guía</div><div class="v">${esc(c.tracking)}</div></div>
  </div>
  <div class="grid">
    <div class="field"><div class="lbl">Código de casillero</div><div class="val">${esc(c.casillero)}</div></div>
    <div class="field"><div class="lbl">Tipo de reclamo</div><div class="val">${esc(c.tipo_reclamo)}</div></div>
    <div class="field"><div class="lbl">Nombre y apellido</div><div class="val">${esc(c.nombre)}</div></div>
    <div class="field"><div class="lbl">WhatsApp</div><div class="val">${esc(c.whatsapp)}</div></div>
    <div class="field"><div class="lbl">Correo electrónico</div><div class="val">${esc(c.email)}</div></div>
    <div class="field"><div class="lbl">Fecha del reclamo</div><div class="val">${esc(
      (() => {
        try {
          return new Date(c.created_at).toLocaleString("es-VE", { dateStyle: "medium", timeStyle: "short" });
        } catch {
          return c.created_at;
        }
      })()
    )}</div></div>
  </div>
  <div class="block"><div class="lbl">Detalle del reclamo</div><div class="txt">${esc(c.detalle)}</div></div>
  ${imgSection}
  <div class="foot"><span>${esc(cfg.marca)}</span><span>Nº ${esc(c.numero_reclamo)} · ${esc(c.tracking)}</span></div>
  <script>window.onload=function(){setTimeout(function(){window.print();},250);};</script>
</body></html>`;
    w.document.write(html);
    w.document.close();
  };

  const filtered = claims.filter((c) => {
    const isArchived = !!c.archived;
    if (view === "archivados" ? !isArchived : isArchived) {
      return false;
    }
    if (estatusFilter !== "Todos" && (c.estatus || "Iniciado") !== estatusFilter) {
      return false;
    }
    if (paisFilter !== "Todos" && (c.pais || "venezuela") !== paisFilter) {
      return false;
    }
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (
      c.tracking?.toLowerCase().includes(s) ||
      c.numero_reclamo?.toLowerCase().includes(s) ||
      c.casillero?.toLowerCase().includes(s) ||
      c.nombre?.toLowerCase().includes(s) ||
      c.email?.toLowerCase().includes(s)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [q, estatusFilter, paisFilter, view]);

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("es-VE", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-dashboard">
      <header className="bg-inkdeep border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-sm bg-gold">
              <Package className="h-5 w-5 text-ink" strokeWidth={2.4} />
            </span>
            <span className="font-display text-lg font-extrabold text-white tracking-tight">
              Reclamos · Admin
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-white/60 font-mono">{user.email}</span>
            <button
              onClick={load}
              data-testid="refresh-button"
              className="text-white/70 hover:text-gold transition-colors"
              aria-label="Actualizar"
            >
              <RefreshCw className={`h-5 w-5 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={onLogout}
              data-testid="logout-button"
              className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-alert transition-colors"
            >
              <LogOut className="h-4 w-4" /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          <StatCard label="Reclamos activos" value={stats?.active_count ?? "—"} />
          <StatCard label="Archivados (cerrados)" value={stats?.archived_count ?? "—"} />
          <StatCard label="Con archivo adjunto" value={stats?.with_files ?? "—"} />
          <StatCard
            label="Tiempo promedio de cierre"
            value={fmtDuration(stats?.avg_resolution_seconds)}
          />
        </div>

        <div className="mb-6 inline-flex rounded-sm border border-ink/15 bg-white p-1" data-testid="view-tabs">
          <button
            onClick={() => setView("activos")}
            data-testid="tab-activos"
            className={`px-4 py-2 text-sm font-semibold rounded-sm transition-colors ${
              view === "activos" ? "bg-ink text-white" : "text-ink hover:bg-paper"
            }`}
          >
            Activos
          </button>
          <button
            onClick={() => setView("archivados")}
            data-testid="tab-archivados"
            className={`px-4 py-2 text-sm font-semibold rounded-sm transition-colors ${
              view === "archivados" ? "bg-ink text-white" : "text-ink hover:bg-paper"
            }`}
          >
            Archivados
          </button>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <h2 className="font-display text-2xl font-extrabold text-ink tracking-tight">
            {view === "archivados" ? "Reclamos archivados" : "Reclamos activos"}
          </h2>
          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            <Select value={paisFilter} onValueChange={setPaisFilter}>
              <SelectTrigger
                data-testid="pais-filter"
                className="h-11 w-[160px] bg-white border border-border rounded-sm text-sm"
              >
                <SelectValue placeholder="País" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="Todos" className="text-sm">Todos los países</SelectItem>
                <SelectItem value="venezuela" className="text-sm">🇻🇪 Venezuela</SelectItem>
                <SelectItem value="nicaragua" className="text-sm">🇳🇮 Nicaragua</SelectItem>
              </SelectContent>
            </Select>
            <Select value={estatusFilter} onValueChange={setEstatusFilter}>
              <SelectTrigger
                data-testid="estatus-filter"
                className="h-11 w-[210px] bg-white border border-border rounded-sm text-sm"
              >
                <SelectValue placeholder="Filtrar por estatus" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="Todos" className="text-sm">Todos los estatus</SelectItem>
                {ESTATUS.map((s) => (
                  <SelectItem key={s} value={s} className="text-sm">
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar tracking, casillero, nombre…"
                data-testid="admin-search"
                className="h-11 w-full bg-white border border-border rounded-sm pl-9 pr-3 text-sm text-ink outline-none focus:border-ink transition-colors"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-muted-foreground" data-testid="admin-empty">
            <Inbox className="h-10 w-10 mb-3" />
            <p>No hay reclamos {q ? "que coincidan con la búsqueda" : "todavía"}.</p>
          </div>
        ) : (
          <div className="bg-white border border-ink/10 rounded-md overflow-auto max-h-[65vh]" data-testid="claims-table">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-ink/10 text-left bg-white">
                  {["Fecha", "País", "Nº Reclamo", "Tracking", "Casillero", "Nombre", "Tipo", "Estatus", "Contacto", "Detalle", "Adjunto", "Acción"].map((h) => (
                    <th key={h} className="px-4 py-3 font-mono text-xs uppercase tracking-wider text-muted-foreground whitespace-nowrap bg-white border-b border-ink/10">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paged.map((c) => (
                  <tr key={c.id} className="border-b border-ink/5 hover:bg-paper transition-colors align-top" data-testid={`claim-row-${c.tracking}`}>
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{fmt(c.created_at)}</td>
                    <td className="px-4 py-3 whitespace-nowrap" data-testid={`pais-${c.tracking}`}>
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-ink/10 bg-paper px-2.5 py-1 text-xs font-medium text-ink">
                        <span className="text-base leading-none">
                          {(COUNTRY_META[c.pais] || COUNTRY_META.venezuela).flag}
                        </span>
                        {(COUNTRY_META[c.pais] || COUNTRY_META.venezuela).label}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono font-semibold text-ink whitespace-nowrap">{c.numero_reclamo || "—"}</td>
                    <td className="px-4 py-3 font-mono font-medium text-ink whitespace-nowrap">{c.tracking}</td>
                    <td className="px-4 py-3 font-mono text-ink whitespace-nowrap">{c.casillero}</td>
                    <td className="px-4 py-3 text-ink whitespace-nowrap">{c.nombre}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="inline-block rounded-full bg-gold/20 text-ink px-2.5 py-0.5 text-xs font-medium">
                        {c.tipo_reclamo}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Select
                        value={c.estatus || "Iniciado"}
                        onValueChange={(v) => changeEstatus(c, v)}
                        disabled={updatingEstatusId === c.id}
                      >
                        <SelectTrigger
                          data-testid={`estatus-select-${c.tracking}`}
                          className={`h-9 w-[200px] text-xs rounded-sm font-medium ${estatusColor(c.estatus)}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {ESTATUS.map((s) => (
                            <SelectItem key={s} value={s} className="text-xs">
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-ink">{c.whatsapp}</div>
                      <div className="text-muted-foreground text-xs">{c.email}</div>
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <p className="text-ink/80 line-clamp-3">{c.detalle}</p>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {c.screenshot_path ? (
                        <button
                          onClick={() => viewFile(c.screenshot_path)}
                          data-testid={`view-file-${c.tracking}`}
                          className="inline-flex items-center gap-1.5 text-ink font-medium hover:text-gold transition-colors"
                        >
                          <FileImage className="h-4 w-4" /> Ver
                        </button>
                      ) : (
                        <span className="text-muted-foreground/50 inline-flex items-center gap-1.5">
                          <Paperclip className="h-4 w-4" />—
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex flex-col gap-2">
                        <button
                          onClick={() => resend(c.id, c.numero_reclamo)}
                          disabled={resendingId === c.id}
                          data-testid={`resend-webhook-${c.tracking}`}
                          title="Reenvía los datos de este reclamo al webhook de Zapier"
                          className="inline-flex items-center justify-center gap-1.5 rounded-sm border border-ink/15 bg-white px-3 py-1.5 text-xs font-semibold text-ink hover:border-ink hover:bg-paper transition-colors disabled:opacity-60"
                        >
                          {resendingId === c.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <ScanLine className="h-3.5 w-3.5 text-gold" />
                          )}
                          Volver a revisar OCR
                        </button>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => printClaim(c)}
                            data-testid={`print-claim-${c.tracking}`}
                            title="Imprimir hoja del reclamo"
                            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-sm border border-ink/15 bg-white px-3 py-1.5 text-xs font-semibold text-ink hover:border-ink hover:bg-paper transition-colors"
                          >
                            <Printer className="h-3.5 w-3.5 text-ink" />
                            Imprimir
                          </button>
                          <button
                            onClick={() => openEmail(c)}
                            data-testid={`email-claim-${c.tracking}`}
                            title="Enviar correo al cliente"
                            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-sm border border-ink/15 bg-white px-3 py-1.5 text-xs font-semibold text-ink hover:border-ink hover:bg-paper transition-colors"
                          >
                            <Mail className="h-3.5 w-3.5 text-ink" />
                            Correo
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setNotesClaimId(c.id);
                              setNoteInput("");
                            }}
                            data-testid={`notes-claim-${c.tracking}`}
                            title="Notas del reclamo"
                            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-sm border border-ink/15 bg-white px-3 py-1.5 text-xs font-semibold text-ink hover:border-ink hover:bg-paper transition-colors"
                          >
                            <StickyNote className="h-3.5 w-3.5 text-gold" />
                            Notas
                            {(c.notas || []).length > 0 && (
                              <span className="ml-0.5 rounded-full bg-ink text-white text-[10px] px-1.5">
                                {(c.notas || []).length}
                              </span>
                            )}
                          </button>
                          <button
                            onClick={() => deleteClaim(c.id, c.numero_reclamo)}
                            disabled={deletingId === c.id}
                            data-testid={`delete-claim-${c.tracking}`}
                            title="Eliminar reclamo"
                            className="inline-flex items-center justify-center rounded-sm border border-alert/30 bg-white px-2.5 py-1.5 text-alert hover:bg-alert hover:text-white transition-colors disabled:opacity-60"
                          >
                            {deletingId === c.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="h-3.5 w-3.5" />
                            )}
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3" data-testid="pagination">
            <p className="text-xs text-muted-foreground font-mono">
              Mostrando {(currentPage - 1) * PAGE_SIZE + 1}–
              {Math.min(currentPage * PAGE_SIZE, filtered.length)} de {filtered.length} reclamos
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                data-testid="page-prev"
                className="inline-flex items-center gap-1.5 rounded-sm border border-ink/15 bg-white px-3 py-1.5 text-sm font-semibold text-ink hover:border-ink hover:bg-paper transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" /> Anterior
              </button>
              <span className="text-sm font-mono text-ink px-2" data-testid="page-info">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                data-testid="page-next"
                className="inline-flex items-center gap-1.5 rounded-sm border border-ink/15 bg-white px-3 py-1.5 text-sm font-semibold text-ink hover:border-ink hover:bg-paper transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Siguiente <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </main>

      <Dialog open={!!notesClaimId} onOpenChange={(o) => !o && setNotesClaimId(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="notes-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-ink">
              Notas del reclamo
            </DialogTitle>
            <DialogDescription>
              Historial de notas internas sobre el proceso de este reclamo.
            </DialogDescription>
          </DialogHeader>
          {(() => {
            const nc = claims.find((c) => c.id === notesClaimId);
            const notas = (nc?.notas || []).slice().reverse();
            return (
              <div className="flex flex-col gap-4">
                <p className="text-xs font-mono text-muted-foreground">
                  {nc?.numero_reclamo} · {nc?.tracking}
                </p>
                <div className="flex flex-col gap-2 max-h-64 overflow-y-auto">
                  {notas.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">
                      Aún no hay notas. Agrega la primera.
                    </p>
                  ) : (
                    notas.map((n) => (
                      <div key={n.id} className="rounded-sm border border-border bg-paper p-3" data-testid="note-item">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm text-ink whitespace-pre-wrap">{n.texto}</p>
                          <button
                            onClick={() => deleteNote(n.id)}
                            className="text-muted-foreground hover:text-alert transition-colors shrink-0"
                            aria-label="Eliminar nota"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <p className="mt-1.5 text-[11px] text-muted-foreground font-mono">
                          {n.autor} ·{" "}
                          {(() => {
                            try {
                              return new Date(n.created_at).toLocaleString("es-VE", {
                                dateStyle: "short",
                                timeStyle: "short",
                              });
                            } catch {
                              return n.created_at;
                            }
                          })()}
                        </p>
                      </div>
                    ))
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <textarea
                    value={noteInput}
                    onChange={(e) => setNoteInput(e.target.value)}
                    placeholder="Escribe una nota sobre el proceso…"
                    data-testid="note-input"
                    className="min-h-[80px] w-full rounded-sm border border-border bg-paper p-3 text-sm text-ink outline-none focus:border-ink transition-colors resize-y"
                  />
                  <button
                    onClick={addNote}
                    disabled={addingNote || !noteInput.trim()}
                    data-testid="add-note-button"
                    className="self-end inline-flex items-center gap-2 rounded-sm bg-gold px-4 py-2 text-sm font-semibold text-ink hover:-translate-y-0.5 transition-transform disabled:opacity-60"
                  >
                    {addingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : <StickyNote className="h-4 w-4" />}
                    Agregar nota
                  </button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={!!emailClaimId} onOpenChange={(o) => !o && setEmailClaimId(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="email-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-ink">
              Enviar correo al cliente
            </DialogTitle>
            <DialogDescription>
              El asunto y mensaje se envían al flujo de Zapier para notificar al cliente.
            </DialogDescription>
          </DialogHeader>
          {(() => {
            const ec = claims.find((c) => c.id === emailClaimId);
            return (
              <div className="flex flex-col gap-4">
                <p className="text-xs font-mono text-muted-foreground">
                  Para: {ec?.email} · {ec?.numero_reclamo}
                </p>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-ink/70">Asunto</label>
                  <input
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    data-testid="email-subject"
                    className="h-11 w-full rounded-sm border border-border bg-paper px-3 text-sm text-ink outline-none focus:border-ink transition-colors"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-ink/70">Mensaje</label>
                  <textarea
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    placeholder="Escribe el mensaje para el cliente…"
                    data-testid="email-body"
                    className="min-h-[140px] w-full rounded-sm border border-border bg-paper p-3 text-sm text-ink outline-none focus:border-ink transition-colors resize-y"
                  />
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <button
              onClick={sendEmail}
              disabled={sendingEmail || !emailSubject.trim() || !emailBody.trim()}
              data-testid="send-email-button"
              className="inline-flex items-center gap-2 rounded-sm bg-gold px-5 py-2.5 text-sm font-semibold text-ink hover:-translate-y-0.5 transition-transform disabled:opacity-60"
            >
              {sendingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              Enviar correo
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setChecking(false);
      return;
    }
    axios
      .get(`${API}/auth/me`, { headers: authHeaders() })
      .then((res) => setUser(res.data))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setChecking(false));
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-inkdeep flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-gold" />
      </div>
    );
  }

  return user ? (
    <Dashboard user={user} onLogout={logout} />
  ) : (
    <Login onLogin={setUser} />
  );
}

